import { useTranslation } from 'react-i18next';

import { TextArea } from '../../../../components/TextArea';
import { useGroupedMetrics } from '../../../../features/preferences/useMetricsLabelsQuery';
import { useDispatchTrackEventWithLocation } from '../../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { MetricsGroups } from '../../Preferences/Metrics/MetricsGroups';
import { CountriesSelectStep } from '../CountriesSelectStep';

import type { SamSettings } from './Onboarding';
import { OnboardingStep } from './Onboarding';
import { StyledMetricsWrapper } from './styles';

export type StepInputProps = {
  step: OnboardingStep;
  settings: SamSettings;
  onChange(settings: SamSettings): void;
  isLoadingVpAndPp: boolean;
};

const ppPlaceholderKey = 'insights.onboarding.pain_point.placeholder';
const vpPlaceholderKey = 'insights.onboarding.value_proposition.placeholder';

export const StepInput = ({ step, settings, onChange, isLoadingVpAndPp }: StepInputProps) => {
  const dispatchTrackEvent = useDispatchTrackEventWithLocation();
  const { t } = useTranslation();
  const groupedMetrics = useGroupedMetrics();

  if ([OnboardingStep.painPoint, OnboardingStep.valueProposition].includes(step)) {
    const value = settings[step] as string;
    const handleTextChange = (newValue: string) => {
      onChange({ ...settings, [step]: newValue });
    };

    const placeholderKey = step === OnboardingStep.painPoint ? ppPlaceholderKey : vpPlaceholderKey;

    return (
      <TextArea
        placeholder={t(placeholderKey)}
        value={value}
        onChange={handleTextChange}
        height={200}
        isLoading={isLoadingVpAndPp}
      />
    );
  }

  if (step === OnboardingStep.metrics) {
    const handleMetricClick = (metric: string) => {
      const isMetricSelected = settings.metrics.includes(metric);
      const metrics = isMetricSelected ? settings.metrics.filter((m) => m !== metric) : [...settings.metrics, metric];

      const action = isMetricSelected ? 'remove metric' : 'add metric';
      dispatchTrackEvent('preferences', action, `${metric}/${metrics.length}`);
      onChange({ ...settings, metrics });
    };

    return (
      <StyledMetricsWrapper>
        <MetricsGroups
          selectedMetrics={settings.metrics}
          groupedMetrics={groupedMetrics}
          onMetricClick={handleMetricClick}
        />
      </StyledMetricsWrapper>
    );
  }

  if (step === OnboardingStep.countries) {
    const handleCountriesChange = (countries: number[]) => {
      onChange({ ...settings, countries });
    };

    return <CountriesSelectStep countries={settings.countries} onChange={handleCountriesChange} />;
  }

  return <></>;
};
